/**
 * Button, with a built-in pending state.
 *
 * `loading` disables the button and shows a spinner, which is what stops a
 * double-submit creating two purchase orders — the single most common
 * data-integrity bug in a form-heavy application.
 *
 * Variants: default, primary, danger, ghost — plus the names screens were
 * written with (secondary, outline, subtle, success, warning, info,
 * danger-ghost), each a look and nothing more. Sizes: xs, sm, default, lg.
 */
import { Link } from 'react-router-dom';

const VARIANTS = {
  default: 'btn',
  primary: 'btn btn-primary',
  danger: 'btn btn-danger',
  ghost: 'btn btn-ghost',
  secondary: 'btn btn-secondary',
  outline: 'btn btn-outline',
  subtle: 'btn btn-subtle',
  success: 'btn btn-tone-success',
  warning: 'btn btn-tone-warning',
  info: 'btn btn-tone-info',
  'danger-ghost': 'btn btn-ghost btn-ghost-danger',
};

const SIZES = { xs: 'btn-xs', sm: 'btn-sm', lg: 'btn-lg' };

function classesFor({ variant, destructive, size, block, iconOnly, className }) {
  return [
    // `destructive` is how a few screens say "danger".
    VARIANTS[destructive ? 'danger' : variant] ?? VARIANTS.default,
    SIZES[size],
    block && 'btn-block',
    iconOnly && 'btn-icon',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

export function Button({
  variant = 'default',
  size,
  loading = false,
  disabled,
  block,
  icon,
  destructive,
  children,
  className = '',
  type = 'button',
  ...rest
}) {
  const classes = classesFor({ variant, destructive, size, block, iconOnly: !children && icon, className });

  return (
    <button type={type} className={classes} disabled={disabled || loading} aria-busy={loading} {...rest}>
      {loading ? <span className="spinner" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
}

/** A link that looks like a button, for navigation rather than an action. */
export function LinkButton({ to, variant = 'default', size, icon, destructive, children, className = '', ...rest }) {
  const classes = classesFor({ variant, destructive, size, className });

  return (
    <Link to={to} className={classes} {...rest}>
      {icon}
      {children}
    </Link>
  );
}
